import type { dealerSearchIncoming } from "@dealer-center/ddc-pages-account-search-interface";
import { verticalTypeMapping } from "@dealer-center/ddc-pages-account-search-interface";
import type { UseLocalStorageReducerDispatch } from "@dealer-center/core-components";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Button,
  ComboBox,
  DatePicker,
  useToaster
} from "@dealer-center/core-components";
import { useContext, useEffect, useState } from "react";
import { StyledPagesClientSearchFilters } from "./filtersStyles";

import {
  accountTypesOptions,
  controlCenterPlatformsOptions,
  countryOptions,
  statesOptions,
  statusesOptions,
  useSearchFilterHelper
} from "./helpers/";
import { GroupedFilters } from "./GroupedFilters";
import styled from "styled-components";
import { getDealerSearchFilters } from "@dealer-center/ddc-pages-account-search-fetch";
import { FiltersChipCard } from "./FiltersChipCard";
import { AccountSearchContext } from "@dealer-center/ddc-pages-account-search-context";

export interface PagesClientSearchFiltersContentProps {
  dispatchAccountSearchFavorites: UseLocalStorageReducerDispatch<dealerSearchIncoming>;
  pinFilters: boolean;
  setPinFilters: (pinFilters: boolean) => void;
  isDesktop: boolean;
}
interface options {
  value: string;
  label: string;
}

const StyledCard = styled.div`
  > * {
    margin-bottom: 8px;
  }
`;
const StyledRightAlignedButton = styled.div`
  display: flex;
  justify-content: flex-end;
`;
export const PagesClientSearchFiltersContent = ({
  isDesktop,
  dispatchAccountSearchFavorites,
  pinFilters,
  setPinFilters
}: PagesClientSearchFiltersContentProps) => {
  const [resourcesOptions, setResourcesOptions] = useState<options[]>([]);
  const [dealerCodesOptions, setDealerCodesOptions] = useState<options[]>([]);
  const [franchisesOptions, setFranchisesOptions] = useState<options[]>([]);
  const { toastError } = useToaster();
  const { isSearchDefault } = useContext(AccountSearchContext);
  const {
    accountTypes,
    urls,
    statuses,
    dealerCodes,
    id,
    name,
    postalCode,
    verticalTypes,
    resources,
    franchises,
    states,
    countries,
    created,
    controlCenterPlatforms
  } = useSearchFilterHelper();
  useEffect(() => {
    const mapStringArrayToOptions = (array: string[]) => {
      return array.map(item => {
        return { value: item, label: item };
      });
    };
    async function fetchFilters() {
      try {
        const response = await getDealerSearchFilters();
        if (!response) return;
        if (response.franchises)
          setFranchisesOptions(mapStringArrayToOptions(response.franchises));
        if (response.resources)
          setResourcesOptions(mapStringArrayToOptions(response.resources));
        if (response.dealerCodeTypes)
          setDealerCodesOptions(
            mapStringArrayToOptions(response.dealerCodeTypes)
          );
      } catch (error: unknown) {
        const message = `${
          (error as Error).message
        }: reading dealer search filters`;
        toastError(message);
      }
    }
    fetchFilters();
  }, []);

  return (
    <StyledPagesClientSearchFilters>
      {isDesktop && (
        <StyledRightAlignedButton>
          <Button
            analyticsConfiguration={{
              eventName: `Account Search Pin Filters ${
                pinFilters ? "Unpin" : "Pin"
              }`
            }}
            buttonStyle="aux"
            onClick={() => setPinFilters(!pinFilters)}
          >
            {pinFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </StyledRightAlignedButton>
      )}
      <AccordionGroup accordionType="single-expanded">
        {isSearchDefault ? (
          <></>
        ) : (
          <FiltersChipCard
            dispatchAccountSearchFavorites={dispatchAccountSearchFavorites}
          />
        )}
        <StyledCard>
          <ComboBox
            allowNewEntry
            label="Account ID"
            multiple={true}
            name="PagesClientSearchFiltersComboBoxID"
            options={id.value}
            value={id.value}
            onChange={id.handleChange}
          />
          <ComboBox
            allowNewEntry
            label="Company Name"
            multiple={true}
            name="PagesClientSearchFiltersComboBoxName"
            options={name.value}
            value={name.value}
            onChange={name.handleChange}
          />
          <ComboBox
            allowNewEntry
            label="URL"
            multiple={true}
            name="PagesClientSearchFiltersComboBoxUrls"
            options={urls.value}
            value={urls.value}
            onChange={urls.handleChange}
          />
          <ComboBox
            label="Status"
            multiple={true}
            name="PagesClientSearchFiltersComboBoxStatuses"
            options={statusesOptions}
            value={statuses.value}
            onChange={statuses.handleChange}
          />
          <ComboBox
            label="Account Type"
            multiple={true}
            name="PagesClientSearchFiltersComboBoxAccountType"
            options={accountTypesOptions}
            value={accountTypes.value}
            onChange={accountTypes.handleChange}
          />
          <ComboBox
            label="Vertical Type"
            multiple={true}
            name="PagesClientSearchFiltersComboBoxVertialType"
            options={verticalTypeMapping}
            value={verticalTypes.value}
            onChange={verticalTypes.handleChange}
          />
          <ComboBox
            allowNewEntry
            label="Franchises Match All"
            multiple={true}
            name="PagesClientSearchFiltersComboBoxControlFranchisesAll"
            options={franchisesOptions}
            value={franchises.matchAll.value}
            onChange={franchises.matchAll.handleChange}
          />
          <ComboBox
            label="Resources Match All"
            multiple={true}
            name="PagesClientSearchFiltersComboBoxControlResourcesAll"
            options={resourcesOptions}
            value={resources.matchAll.value}
            onChange={resources.matchAll.handleChange}
          />
          <ComboBox
            allowNewEntry
            label="Dealer Code"
            multiple={true}
            name="PagesClientSearchFiltersComboBoxControlDealerCodessAll"
            options={dealerCodesOptions}
            value={dealerCodes.value}
            onChange={dealerCodes.handleChange}
          />
          <ComboBox
            allowNewEntry
            label="Dealer Code Value"
            multiple={true}
            name="PagesClientSearchFiltersComboBoxControlDealerCodesValueAll"
            options={dealerCodes.values.value}
            value={dealerCodes.values.value}
            onChange={dealerCodes.values.handleChange}
          />
        </StyledCard>

        <Accordion>
          <AccordionSummary>Additional Filters</AccordionSummary>
          <AccordionDetails>
            <ComboBox
              label="ControlCenter Platforms"
              multiple={true}
              name="PagesClientSearchFiltersComboBoxControlCenterPlatforms"
              options={controlCenterPlatformsOptions}
              value={controlCenterPlatforms.value}
              onChange={controlCenterPlatforms.handleChange}
            />
            <GroupedFilters title="Address">
              <ComboBox
                label="Country"
                multiple={true}
                name="PagesClientSearchFiltersComboBoxCountry"
                options={countryOptions}
                value={countries.value}
                onChange={countries.handleChange}
              />
              <ComboBox
                label="State"
                multiple={true}
                name="PagesClientSearchFiltersComboBoxStates"
                options={statesOptions}
                value={states.value}
                onChange={states.handleChange}
              />
              <ComboBox
                allowNewEntry
                label="Postal Code"
                multiple={true}
                name="PagesClientSearchFiltersComboBoxPostalCode"
                options={postalCode.value}
                value={postalCode.value}
                onChange={postalCode.handleChange}
              />
            </GroupedFilters>
            <GroupedFilters title="Date">
              <DatePicker
                id="PagesClientSearchFiltersDatePickerControlCreatedAfter"
                label="Created After"
                value={created.after.value}
                onChange={created.after.handleChange}
              />
              <DatePicker
                id="PagesClientSearchFiltersDatePickerControlCreatedBefore"
                label="Created Before"
                value={created.before.value}
                onChange={created.before.handleChange}
              />
            </GroupedFilters>
            <GroupedFilters title="Franchises">
              <ComboBox
                allowNewEntry
                label="Franchises Match Any"
                multiple={true}
                name="PagesClientSearchFiltersComboBoxControlFranchisesAny"
                options={franchisesOptions}
                value={franchises.matchAny.value}
                onChange={franchises.matchAny.handleChange}
              />
              <ComboBox
                allowNewEntry
                label="Franchises Match None"
                multiple={true}
                name="PagesClientSearchFiltersComboBoxControlFranchisesNone"
                options={franchisesOptions}
                value={franchises.matchNone.value}
                onChange={franchises.matchNone.handleChange}
              />
            </GroupedFilters>

            <GroupedFilters title="Resources">
              <ComboBox
                allowNewEntry
                label="Resources Match Any"
                multiple={true}
                name="PagesClientSearchFiltersComboBoxControlResourcesAny"
                options={resourcesOptions}
                value={resources.matchAny.value}
                onChange={resources.matchAny.handleChange}
              />
              <ComboBox
                allowNewEntry
                label="Resources Match None"
                multiple={true}
                name="PagesClientSearchFiltersComboBoxControlResourcesNone"
                options={resourcesOptions}
                value={resources.matchNone.value}
                onChange={resources.matchNone.handleChange}
              />
            </GroupedFilters>
          </AccordionDetails>
        </Accordion>
      </AccordionGroup>
    </StyledPagesClientSearchFilters>
  );
};
